import { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Checkbox,
    Chip,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    Spinner,
} from "@heroui/react";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { array, InferType, mixed, number, object, string } from "yup";
import FileInput from "@/components/inputs/FileInput";
import SelectInput from "@/components/inputs/SelectInput";
import SelectMultipleInput from "@/components/inputs/SelectMultipleInput";
import TextAreaInput from "@/components/inputs/TextAreaInput";
import TextInput from "@/components/inputs/TextInput";
import useAuth from "@/lib/auth/useAuth";
import useRequest from "@/lib/request/useRequest";
import {
    CspResource,
    CspResourceCsp,
    CspResourceRecipients,
} from "@/types/cspResource";

const schema = object().shape({
    title: string().trim().required("Enter a title.").max(200).default(""),
    description: string().defined().default(""),
    source: string().oneOf(["file", "link"]).required().default("file"),
    file: mixed<File>()
        .nullable()
        .default(null)
        .when("source", {
            is: "file",
            then: (value) => value.required("Choose a file to upload."),
        }),
    external_url: string()
        .defined()
        .default("")
        .when("source", {
            is: "link",
            then: (value) =>
                value
                    .required("Enter a link.")
                    .url("Enter a valid link.")
                    .matches(/^https?:\/\//i, "Use an HTTP or HTTPS link.")
                    .max(2048),
        }),
    audience: string()
        .oneOf(["all", "organizations", "tiers"])
        .required()
        .default("all"),
    tiers: array()
        .of(string().required())
        .required()
        .default([])
        .when("audience", {
            is: "tiers",
            then: (value) => value.min(1, "Select at least one CSP tier."),
        }),
    organizations: array()
        .of(number().required())
        .required()
        .default([])
        .when("audience", {
            is: "organizations",
            then: (value) => value.min(1, "Select at least one CSP."),
        }),
});

type ResourceFormValues = InferType<typeof schema>;

function errorMessages(value: unknown): string[] {
    if (typeof value === "string") return [value];
    if (Array.isArray(value)) return value.flatMap(errorMessages);
    if (value && typeof value === "object")
        return Object.values(value).flatMap(errorMessages);
    return [];
}

function CspMultiSelect({
    label,
    options,
    value,
    onChange,
    error,
    isDisabled,
}: {
    label: string;
    options: CspResourceCsp[];
    value: number[];
    onChange: (ids: number[]) => void;
    error?: string;
    isDisabled: boolean;
}) {
    const [search, setSearch] = useState("");
    const selected = useMemo(() => new Set(value), [value]);
    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return options.filter((csp) =>
            `${csp.name} ${csp.id}`.toLowerCase().includes(term),
        );
    }, [options, search]);
    const toggle = (id: number, checked: boolean) => {
        onChange(
            checked
                ? [...value, id]
                : value.filter((selectedId) => selectedId !== id),
        );
    };

    return (
        <div className="flex min-w-0 flex-col gap-2">
            <Input
                label={label}
                placeholder="Search CSPs by name or ID…"
                size="md"
                labelPlacement="outside"
                startContent={<FontAwesomeIcon icon={faSearch} />}
                value={search}
                onValueChange={setSearch}
                isClearable
                onClear={() => setSearch("")}
                isDisabled={isDisabled}
                isInvalid={Boolean(error)}
                errorMessage={error}
            />
            {value.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {options
                        .filter((csp) => selected.has(csp.id))
                        .map((csp) => (
                            <Chip
                                key={csp.id}
                                size="sm"
                                variant="flat"
                                color="primary"
                                onClose={
                                    isDisabled
                                        ? undefined
                                        : () => toggle(csp.id, false)
                                }
                            >
                                {csp.name} ({csp.id})
                            </Chip>
                        ))}
                </div>
            )}
            <div
                className="flex max-h-48 flex-col gap-2 overflow-y-auto rounded-medium border border-default-200 p-3"
                role="group"
                aria-label={label}
            >
                {filtered.map((csp) => (
                    <Checkbox
                        key={csp.id}
                        size="sm"
                        isSelected={selected.has(csp.id)}
                        onValueChange={(checked) => toggle(csp.id, checked)}
                        isDisabled={isDisabled}
                    >
                        {csp.name} ({csp.id})
                    </Checkbox>
                ))}
                {filtered.length === 0 && (
                    <p className="text-sm text-default-400">No CSPs found.</p>
                )}
            </div>
        </div>
    );
}

function CspResourceForm({
    onClose,
    csp,
}: {
    onClose: () => void;
    csp?: CspResourceCsp;
}) {
    const { userIsSentinel } = useAuth();
    const { useGetRequest, useMutateRequest } = useRequest();
    const queryClient = useQueryClient();
    const f = useForm<ResourceFormValues>({
        resolver: yupResolver(schema),
        defaultValues: csp
            ? {
                  ...schema.getDefault(),
                  audience: "organizations",
                  organizations: [csp.id],
              }
            : schema.getDefault(),
    });
    const { setValue, clearErrors } = f;
    const source = f.watch("source");
    const audience = f.watch("audience");
    const file = f.watch("file");
    const choices = useGetRequest<CspResourceRecipients>({
        url: "/csp_resources/recipients/",
        enabled: userIsSentinel,
    });
    const sendResource = useMutateRequest<CspResource>({
        successMessage: "Resource sent",
        errorMessage:
            "Unable to send this resource. Please check the form and try again.",
    });

    useEffect(() => {
        if (source === "file") {
            setValue("external_url", "");
        } else {
            setValue("file", null);
        }
        clearErrors(["file", "external_url"]);
        clearErrors("root");
    }, [source, setValue, clearErrors]);

    useEffect(() => {
        if (audience !== "organizations") setValue("organizations", []);
        if (audience !== "tiers") setValue("tiers", []);
        clearErrors(["tiers", "organizations"]);
        clearErrors("root");
    }, [audience, setValue, clearErrors]);

    const onSubmit = (data: ResourceFormValues) => {
        if (!userIsSentinel || sendResource.isPending) return;
        clearErrors();
        const formData = new FormData();
        formData.append("title", data.title);
        formData.append("description", data.description);
        formData.append("source", data.source);
        formData.append("audience", data.audience);
        if (data.source === "file" && data.file) {
            formData.append("file", data.file);
        } else if (data.source === "link") {
            formData.append("external_url", data.external_url);
        }
        if (data.audience === "organizations") {
            data.organizations.forEach((id) =>
                formData.append("organizations", String(id)),
            );
        }
        if (data.audience === "tiers") {
            data.tiers.forEach((tier) => formData.append("tiers", tier));
        }
        sendResource.mutate(
            { url: "/csp_resources/", method: "post", data: formData },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries({
                        queryKey: ["/csp_resources/"],
                    });
                    queryClient.invalidateQueries({
                        queryKey: ["/csp_resources/recipients/"],
                    });
                    onClose();
                },
                onError: (error) => {
                    let detail: unknown;
                    try {
                        detail = JSON.parse(error.message);
                    } catch {
                        f.setError("root", {
                            message:
                                "Unable to send this resource. Please try again.",
                        });
                        return;
                    }
                    const rootErrors: string[] = [];
                    if (
                        detail &&
                        typeof detail === "object" &&
                        !Array.isArray(detail)
                    ) {
                        for (const [key, value] of Object.entries(detail)) {
                            const message = errorMessages(value).join(" ");
                            if (
                                Object.prototype.hasOwnProperty.call(
                                    schema.fields,
                                    key,
                                )
                            ) {
                                f.setError(key as keyof ResourceFormValues, {
                                    message,
                                });
                            } else {
                                rootErrors.push(message);
                            }
                        }
                    } else {
                        rootErrors.push(...errorMessages(detail));
                    }
                    if (rootErrors.length || !detail) {
                        f.setError("root", {
                            message:
                                rootErrors.join(" ") ||
                                "Unable to send this resource. Please try again.",
                        });
                    }
                },
            },
        );
    };

    if (!userIsSentinel) return null;

    const pending = sendResource.isPending;
    const errors = f.formState.errors;

    function handleClose() {
        if (!pending) onClose();
    }

    return (
        <Modal
            isOpen
            onClose={handleClose}
            isDismissable={!pending}
            isKeyboardDismissDisabled={pending}
            hideCloseButton={pending}
            size="lg"
            scrollBehavior="inside"
        >
            <ModalContent>
                <ModalHeader>Send a resource</ModalHeader>
                <ModalBody>
                    {choices.isPending ? (
                        <div className="flex h-full min-h-0 items-center justify-center p-6">
                            <Spinner label="Loading CSPs" />
                        </div>
                    ) : choices.isError ? (
                        <Alert
                            color="danger"
                            title="We couldn’t load CSPs."
                            endContent={
                                <Button
                                    size="sm"
                                    variant="flat"
                                    color="primary"
                                    isLoading={choices.isFetching}
                                    onPress={() => choices.refetch()}
                                >
                                    Try again
                                </Button>
                            }
                        />
                    ) : (
                        <FormProvider {...f}>
                            <form
                                onSubmit={f.handleSubmit(onSubmit)}
                                className="flex flex-col gap-2"
                            >
                                {errors.root?.message && (
                                    <Alert
                                        color="danger"
                                        title={errors.root.message}
                                    />
                                )}
                                <TextInput
                                    name="title"
                                    label="Title"
                                    placeholder="Enter a resource title"
                                    size="sm"
                                    labelPlacement="outside"
                                    maxLength={200}
                                    isRequired
                                    isDisabled={pending}
                                />
                                <TextAreaInput
                                    name="description"
                                    label="Description"
                                    size="sm"
                                    labelPlacement="outside"
                                    minRows={2}
                                    isDisabled={pending}
                                    isInvalid={Boolean(errors.description)}
                                    errorMessage={errors.description?.message}
                                />
                                <SelectInput
                                    name="source"
                                    label="Resource type"
                                    size="sm"
                                    labelPlacement="outside"
                                    options={[
                                        { id: "file", description: "File" },
                                        { id: "link", description: "Link" },
                                    ]}
                                    isRequired
                                    isDisabled={pending}
                                />
                                {source === "file" ? (
                                    <div className="flex flex-col gap-1">
                                        <p className="text-sm font-medium">
                                            File
                                        </p>
                                        <div className="flex flex-col items-start gap-2">
                                            <FileInput
                                                label="Choose file"
                                                size="sm"
                                                variant="flat"
                                                isDisabled={pending}
                                                onSelect={(selected) => {
                                                    if (
                                                        selected instanceof File
                                                    ) {
                                                        setValue(
                                                            "file",
                                                            selected,
                                                            {
                                                                shouldValidate:
                                                                    true,
                                                            },
                                                        );
                                                    }
                                                }}
                                            />
                                            {file && (
                                                <p className="break-all text-sm">
                                                    {file.name}
                                                </p>
                                            )}
                                            <p className="text-sm text-gray-500">
                                                Any file type. Up to 1 GB.
                                            </p>
                                            {errors.file?.message && (
                                                <p
                                                    role="alert"
                                                    className="text-sm text-danger"
                                                >
                                                    {errors.file.message}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <TextInput
                                        name="external_url"
                                        label="Link"
                                        size="sm"
                                        labelPlacement="outside"
                                        placeholder="https://…"
                                        isRequired
                                        isDisabled={pending}
                                    />
                                )}
                                {csp ? (
                                    <Input
                                        label="Send to"
                                        value={csp.name}
                                        size="sm"
                                        labelPlacement="outside"
                                        isReadOnly
                                        isInvalid={Boolean(
                                            errors.organizations,
                                        )}
                                        errorMessage={
                                            errors.organizations?.message
                                        }
                                    />
                                ) : (
                                    <SelectInput
                                        name="audience"
                                        label="Send to"
                                        size="sm"
                                        labelPlacement="outside"
                                        options={[
                                            {
                                                id: "all",
                                                description: "All CSPs",
                                            },
                                            {
                                                id: "organizations",
                                                description: "Selected CSPs",
                                            },
                                            {
                                                id: "tiers",
                                                description: "CSP tiers",
                                            },
                                        ]}
                                        isRequired
                                        isDisabled={pending}
                                    />
                                )}
                                {!csp && audience === "tiers" && (
                                    <SelectMultipleInput
                                        name="tiers"
                                        label="CSP tiers"
                                        size="sm"
                                        labelPlacement="outside"
                                        options={choices.data?.tiers ?? []}
                                        isRequired
                                        isDisabled={pending}
                                    />
                                )}
                                {!csp && audience === "organizations" && (
                                    <div className="grid gap-2">
                                        <Controller
                                            name="organizations"
                                            control={f.control}
                                            render={({ field, fieldState }) => (
                                                <CspMultiSelect
                                                    label="CSPs"
                                                    options={
                                                        choices.data?.csps ?? []
                                                    }
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    error={
                                                        fieldState.error
                                                            ?.message
                                                    }
                                                    isDisabled={pending}
                                                />
                                            )}
                                        />
                                    </div>
                                )}
                                <div className="flex justify-end gap-2 pt-6">
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="flat"
                                        onPress={handleClose}
                                        isDisabled={pending}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        color="primary"
                                        size="sm"
                                        isLoading={pending}
                                        isDisabled={pending}
                                    >
                                        Send
                                    </Button>
                                </div>
                            </form>
                        </FormProvider>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}

export default CspResourceForm;
